import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Plus, Loader2, ArrowLeft, Upload, Trash2, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Vegetables', 'Grains', 'Tubers', 'Fruits', 'Livestock', 'Poultry', 'Fish', 'Dairy', 'Nuts', 'Herbs & Spices'];
const STATUS = {
  pending: { label: 'Under review', icon: Clock, color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Live on market', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-800' },
};

const FarmerListings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ profile: null, listings: [] });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', category: 'Vegetables', unit: 'kg',
    price: '', quantity_available: '', description: '',
    pickup_location: '', harvest_date: '',
  });
  const [photo, setPhoto] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/farmer/me`);
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/auth?redirect=/farmer/listings', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>;

  if (!data.profile || data.profile.status !== 'approved') {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center" data-testid="listings-not-approved">
        <Card>
          <CardContent className="p-8">
            <p className="text-gray-500 mb-3">You need to be an approved farmer to manage listings.</p>
            <Link to="/farmer/apply"><Button className="bg-green-600 hover:bg-green-700">Apply now</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.quantity_available) {
      toast.error('Name, price and quantity are required');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append('photo', photo);
    try {
      await axios.post(`${API}/farmer/commodities`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Commodity submitted — pending admin review.');
      setShowForm(false);
      setForm({ name: '', category: 'Vegetables', unit: 'kg', price: '', quantity_available: '', description: '', pickup_location: '', harvest_date: '' });
      setPhoto(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create listing');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      await axios.delete(`${API}/farmer/commodities/${id}`);
      toast.success('Listing removed');
      await load();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="farmer-listings-page">
      <Link to="/dashboard" className="text-sm text-gray-500 hover:text-gray-700 mb-3 inline-flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">My commodity listings</h1>
          <p className="text-sm text-gray-500">{data.profile.farm_name} · {data.profile.location}</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700" data-testid="new-listing-btn">
          <Plus className="h-4 w-4 mr-1" /> New listing
        </Button>
      </div>

      {data.listings.length === 0 && !showForm ? (
        <Card><CardContent className="p-12 text-center">
          <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 mb-4">No listings yet. Post your first commodity to reach thousands of buyers.</p>
          <Button onClick={() => setShowForm(true)} className="bg-green-600 hover:bg-green-700">Add a commodity</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.listings.map(l => {
            const st = STATUS[l.status] || STATUS.pending;
            const Icon = st.icon;
            return (
              <Card key={l._id} data-testid={`listing-row-${l._id}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div>
                      <p className="font-semibold">{l.name}</p>
                      <p className="text-xs text-gray-500">{l.category}</p>
                    </div>
                    <Badge className={`${st.color} border-none`}><Icon className="h-3 w-3 mr-1" />{st.label}</Badge>
                  </div>
                  <p className="text-sm">₦{Number(l.price).toLocaleString()}/{l.unit} · {l.quantity_available} available</p>
                  {l.admin_notes && l.status === 'rejected' && (
                    <p className="text-xs text-red-700 mt-2 bg-red-50 p-2 rounded">Notes: {l.admin_notes}</p>
                  )}
                  <div className="flex justify-end mt-2">
                    <Button variant="ghost" size="sm" onClick={() => remove(l._id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" data-testid="new-listing-modal">
          <Card className="w-full max-w-lg my-8">
            <CardHeader><CardTitle>New commodity</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold block mb-1">Name*</label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="listing-name" /></div>
                  <div>
                    <label className="text-xs font-semibold block mb-1">Category</label>
                    <select className="w-full border rounded-md p-2 text-sm" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} data-testid="listing-category">
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs font-semibold block mb-1">Unit</label><Input value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))} placeholder="kg / crate" /></div>
                  <div><label className="text-xs font-semibold block mb-1">Price (₦)*</label><Input type="number" min={0} value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} data-testid="listing-price" /></div>
                  <div><label className="text-xs font-semibold block mb-1">Quantity*</label><Input type="number" min={0} value={form.quantity_available} onChange={e => setForm(f => ({...f, quantity_available: e.target.value}))} data-testid="listing-qty" /></div>
                </div>
                <div><label className="text-xs font-semibold block mb-1">Description</label><textarea className="w-full border rounded-md p-2 text-sm min-h-[80px]" maxLength={2000} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold block mb-1">Pickup location</label><Input value={form.pickup_location} onChange={e => setForm(f => ({...f, pickup_location: e.target.value}))} placeholder={data.profile.location} /></div>
                  <div><label className="text-xs font-semibold block mb-1">Harvest date</label><Input type="date" value={form.harvest_date} onChange={e => setForm(f => ({...f, harvest_date: e.target.value}))} /></div>
                </div>
                <div>
                  <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-md p-3 cursor-pointer hover:border-green-500">
                    <Upload className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-500">{photo ? photo.name : 'Photo (JPG/PNG/WebP)'}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setPhoto(e.target.files?.[0])} className="hidden" />
                  </label>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="listing-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Submit for review
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FarmerListings;
